(() => {
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Bild konnte nicht geladen werden: ${src}`));
      img.src = src;
    });
  }

  function cubicBezierPoint(p0, p1, p2, p3, t) {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;

    return {
      x: (mt2 * mt * p0.x) + (3 * mt2 * t * p1.x) + (3 * mt * t2 * p2.x) + (t2 * t * p3.x),
      y: (mt2 * mt * p0.y) + (3 * mt2 * t * p1.y) + (3 * mt * t2 * p2.y) + (t2 * t * p3.y)
    };
  }

  function lerpPoint(a, b, t) {
    return {
      x: a.x + ((b.x - a.x) * t),
      y: a.y + ((b.y - a.y) * t)
    };
  }

  function ensureFinalWarpHandles(finalWarp) {
    if (finalWarp.handles) {
      return;
    }

    finalWarp.handles = {
      topLeft: lerpPoint(finalWarp.topLeft, finalWarp.topRight, 1 / 3),
      topRight: lerpPoint(finalWarp.topLeft, finalWarp.topRight, 2 / 3),
      bottomRight: lerpPoint(finalWarp.bottomLeft, finalWarp.bottomRight, 2 / 3),
      bottomLeft: lerpPoint(finalWarp.bottomLeft, finalWarp.bottomRight, 1 / 3)
    };
  }

  function getPlacedFinalWarp(finalWarp, placement) {
    return {
      topLeft: {
        x: placement.x + finalWarp.topLeft.x,
        y: placement.y + finalWarp.topLeft.y
      },
      topRight: {
        x: placement.x + finalWarp.topRight.x,
        y: placement.y + finalWarp.topRight.y
      },
      bottomRight: {
        x: placement.x + finalWarp.bottomRight.x,
        y: placement.y + finalWarp.bottomRight.y
      },
      bottomLeft: {
        x: placement.x + finalWarp.bottomLeft.x,
        y: placement.y + finalWarp.bottomLeft.y
      },
      handles: {
        topLeft: {
          x: placement.x + finalWarp.handles.topLeft.x,
          y: placement.y + finalWarp.handles.topLeft.y
        },
        topRight: {
          x: placement.x + finalWarp.handles.topRight.x,
          y: placement.y + finalWarp.handles.topRight.y
        },
        bottomRight: {
          x: placement.x + finalWarp.handles.bottomRight.x,
          y: placement.y + finalWarp.handles.bottomRight.y
        },
        bottomLeft: {
          x: placement.x + finalWarp.handles.bottomLeft.x,
          y: placement.y + finalWarp.handles.bottomLeft.y
        }
      }
    };
  }

  function drawImageStripToQuad(ctx, img, sx, sy, sw, sh, p1, p2, p3, p4, overlap = 1) {
    const dx1 = p2.x - p1.x;
    const dy1 = p2.y - p1.y;
    const dx2 = p4.x - p1.x;
    const dy2 = p4.y - p1.y;

    ctx.save();

    const ex = overlap * 0.5;

    ctx.beginPath();
    ctx.moveTo(p1.x - ex, p1.y);
    ctx.lineTo(p2.x + ex, p2.y);
    ctx.lineTo(p3.x + ex, p3.y);
    ctx.lineTo(p4.x - ex, p4.y);
    ctx.closePath();
    ctx.clip();

    ctx.setTransform(
      dx1 / sw,
      dy1 / sw,
      dx2 / sh,
      dy2 / sh,
      p1.x,
      p1.y
    );

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw + overlap, sh);
    ctx.restore();
  }

  function addLocalPrintTexture(ctx, width, height, alpha, step, intensity) {
    const noiseCanvas = document.createElement('canvas');
    noiseCanvas.width = width;
    noiseCanvas.height = height;

    const nctx = noiseCanvas.getContext('2d');

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const n = Math.floor(128 + (Math.random() - 0.5) * intensity * 8);
        nctx.fillStyle = `rgb(${n}, ${n}, ${n})`;
        nctx.fillRect(x, y, step, step);
      }
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(noiseCanvas, 0, 0);
    ctx.restore();
  }

  function createWarpedDesign(designImage, options = {}) {
    const {
      width = 360,
      height = 190,
      designScale = 1.2,
      designOffsetX = 0,
      designOffsetY = 0,
      topInsetLeft = 18,
      topInsetRight = 6,
      bottomInsetLeft = 6,
      bottomInsetRight = 18,
      topCurveLiftLeft = -18,
      topCurveLiftRight = -4,
      bottomCurveDropLeft = 8,
      bottomCurveDropRight = 16,
      leftSideBulge = 10,
      rightSideBulge = -6,
      verticalBendStrength = 0.16,
      shearX = 0.04,
      strips = 420,
      stripOverlap = 1.3,
      finalWarp = null
    } = options;

    const preparedDesign = document.createElement('canvas');
    preparedDesign.width = width;
    preparedDesign.height = height;

    const pdCtx = preparedDesign.getContext('2d');
    pdCtx.imageSmoothingEnabled = true;
    pdCtx.imageSmoothingQuality = 'high';

    const fit = Math.min(width / designImage.width, height / designImage.height) * designScale;
    const drawW = designImage.width * fit;
    const drawH = designImage.height * fit;
    const drawX = ((width - drawW) / 2) + designOffsetX;
    const drawY = ((height - drawH) / 2) + designOffsetY;

    pdCtx.clearRect(0, 0, width, height);
    pdCtx.drawImage(designImage, drawX, drawY, drawW, drawH);

    const overscan = Math.ceil(Math.max(
      Math.abs(topCurveLiftLeft),
      Math.abs(topCurveLiftRight),
      Math.abs(bottomCurveDropLeft),
      Math.abs(bottomCurveDropRight),
      Math.abs(leftSideBulge),
      Math.abs(rightSideBulge),
      Math.abs(shearX) * width * 0.5,
      width * 0.08,
      height * 0.12
    )) + 8;

    const warpedCanvas = document.createElement('canvas');
    warpedCanvas.width = width + (overscan * 2);
    warpedCanvas.height = height + (overscan * 2);

    const wctx = warpedCanvas.getContext('2d');
    wctx.imageSmoothingEnabled = true;
    wctx.imageSmoothingQuality = 'high';

    const topLeft = { x: overscan + topInsetLeft, y: overscan };
    const topRight = { x: overscan + width - topInsetRight, y: overscan };
    const bottomLeft = { x: overscan + bottomInsetLeft, y: overscan + height };
    const bottomRight = { x: overscan + width - bottomInsetRight, y: overscan + height };

    const topCurve = {
      cp1: { x: overscan + (width * 0.26), y: overscan + topCurveLiftLeft },
      cp2: { x: overscan + (width * 0.76), y: overscan + topCurveLiftRight }
    };

    const bottomCurve = {
      cp1: { x: overscan + (width * 0.24), y: overscan + height + bottomCurveDropLeft },
      cp2: { x: overscan + (width * 0.78), y: overscan + height + bottomCurveDropRight }
    };

    const srcStripW = preparedDesign.width / strips;
    let minWarpX = Infinity;
    let minWarpY = Infinity;
    let maxWarpX = -Infinity;
    let maxWarpY = -Infinity;

    for (let i = 0; i < strips; i++) {
      const t0 = i / strips;
      const t1 = (i + 1) / strips;

      const top0 = cubicBezierPoint(topLeft, topCurve.cp1, topCurve.cp2, topRight, t0);
      const top1 = cubicBezierPoint(topLeft, topCurve.cp1, topCurve.cp2, topRight, t1);
      const bottom0 = cubicBezierPoint(bottomLeft, bottomCurve.cp1, bottomCurve.cp2, bottomRight, t0);
      const bottom1 = cubicBezierPoint(bottomLeft, bottomCurve.cp1, bottomCurve.cp2, bottomRight, t1);

      const tm = (t0 + t1) * 0.5;
      const sideBulge = ((1 - tm) * leftSideBulge) + (tm * rightSideBulge);
      const belly = Math.sin(tm * Math.PI);

      top0.y += belly * height * verticalBendStrength * 0.35;
      top1.y += belly * height * verticalBendStrength * 0.35;
      bottom0.y -= belly * height * verticalBendStrength * 0.18;
      bottom1.y -= belly * height * verticalBendStrength * 0.18;

      top0.x += sideBulge * belly;
      top1.x += sideBulge * belly;
      bottom0.x += sideBulge * belly * 0.9;
      bottom1.x += sideBulge * belly * 0.9;

      const shearOffset0 = (top0.y / height) * shearX * width * 0.2;
      const shearOffset1 = (top1.y / height) * shearX * width * 0.2;

      top0.x += shearOffset0;
      top1.x += shearOffset1;
      bottom0.x += shearOffset0 * 0.4;
      bottom1.x += shearOffset1 * 0.4;

      minWarpX = Math.min(minWarpX, top0.x, top1.x, bottom0.x, bottom1.x);
      minWarpY = Math.min(minWarpY, top0.y, top1.y, bottom0.y, bottom1.y);
      maxWarpX = Math.max(maxWarpX, top0.x, top1.x, bottom0.x, bottom1.x);
      maxWarpY = Math.max(maxWarpY, top0.y, top1.y, bottom0.y, bottom1.y);

      const sx = Math.floor(i * srcStripW);
      const nextSx = Math.ceil((i + 1) * srcStripW);
      const sw = Math.max(1, nextSx - sx);

      drawImageStripToQuad(
        wctx,
        preparedDesign,
        sx,
        0,
        sw,
        preparedDesign.height,
        top0,
        top1,
        bottom1,
        bottom0,
        stripOverlap
      );
    }

    addLocalPrintTexture(wctx, width, height, 0.025, 2, 5);

    const sourceBounds = {
      x: Math.max(0, Math.floor(minWarpX - stripOverlap - 2)),
      y: Math.max(0, Math.floor(minWarpY - 2)),
      width: Math.min(warpedCanvas.width, Math.ceil(maxWarpX - minWarpX + (stripOverlap * 2) + 4)),
      height: Math.min(warpedCanvas.height, Math.ceil(maxWarpY - minWarpY + 4))
    };

    if (finalWarp) {
      return warpCanvasToQuad(
        warpedCanvas,
        sourceBounds.width,
        sourceBounds.height,
        finalWarp,
        width,
        height,
        strips,
        stripOverlap,
        sourceBounds.x,
        sourceBounds.y
      );
    }

    return warpedCanvas;
  }

  function warpCanvasToQuad(
    sourceCanvas,
    sourceWidth,
    sourceHeight,
    quad,
    outputWidth = sourceWidth,
    outputHeight = sourceHeight,
    strips = 420,
    overlap = 1.25,
    sourceX = 0,
    sourceY = 0
  ) {
    const out = document.createElement('canvas');
    out.width = outputWidth;
    out.height = outputHeight;

    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ensureFinalWarpHandles(quad);

    const srcStripW = sourceWidth / strips;

    for (let i = 0; i < strips; i++) {
      const t0 = i / strips;
      const t1 = (i + 1) / strips;

      const top0 = cubicBezierPoint(quad.topLeft, quad.handles.topLeft, quad.handles.topRight, quad.topRight, t0);
      const top1 = cubicBezierPoint(quad.topLeft, quad.handles.topLeft, quad.handles.topRight, quad.topRight, t1);
      const bottom0 = cubicBezierPoint(quad.bottomLeft, quad.handles.bottomLeft, quad.handles.bottomRight, quad.bottomRight, t0);
      const bottom1 = cubicBezierPoint(quad.bottomLeft, quad.handles.bottomLeft, quad.handles.bottomRight, quad.bottomRight, t1);

      const sx = sourceX + Math.floor(i * srcStripW);
      const nextSx = sourceX + Math.ceil((i + 1) * srcStripW);
      const sw = Math.max(1, nextSx - sx);

      drawImageStripToQuad(
        ctx,
        sourceCanvas,
        sx,
        sourceY,
        sw,
        sourceHeight,
        top0,
        top1,
        bottom1,
        bottom0,
        overlap
      );
    }

    return out;
  }

  function applySoftShadingOverlay(ctx, baseImage, placement, multiplyAlpha = 0.1, screenAlpha = 0.03) {
    const shadeCanvas = document.createElement('canvas');
    shadeCanvas.width = ctx.canvas.width;
    shadeCanvas.height = ctx.canvas.height;

    const sctx = shadeCanvas.getContext('2d');
    sctx.drawImage(baseImage, 0, 0);

    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = multiplyAlpha;
    ctx.drawImage(
      shadeCanvas,
      placement.x,
      placement.y,
      placement.width,
      placement.height,
      placement.x,
      placement.y,
      placement.width,
      placement.height
    );
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = screenAlpha;
    ctx.drawImage(
      shadeCanvas,
      placement.x,
      placement.y,
      placement.width,
      placement.height,
      placement.x,
      placement.y,
      placement.width,
      placement.height
    );
    ctx.restore();
  }

  async function renderToCanvas({
    canvasEl,
    baseSrc,
    designSrc,
    config = window.VanscapeGrosserBaumwollbeutelMockup?.defaultConfig
  }) {
    const [baseImage, designImage] = await Promise.all([
      loadImage(baseSrc),
      loadImage(designSrc)
    ]);

    const ctx = canvasEl.getContext('2d');
    canvasEl.width = baseImage.width;
    canvasEl.height = baseImage.height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    ctx.drawImage(baseImage, 0, 0);

    const { placement, warpOptions, shading } = config;
    // The front of this bag is viewed almost straight on. A uniform contained
    // scale keeps the original design proportions; the former rectangular
    // warp made vans look unnaturally wide.
    const uniformScale = Math.min(
      placement.width / designImage.width,
      placement.height / designImage.height
    ) * (warpOptions.designScale || 1);
    const designWidth = designImage.width * uniformScale;
    const designHeight = designImage.height * uniformScale;
    const designX = placement.x + ((placement.width - designWidth) / 2) + (warpOptions.designOffsetX || 0);
    const designY = placement.y + ((placement.height - designHeight) / 2) + (warpOptions.designOffsetY || 0);
    ctx.drawImage(designImage, designX, designY, designWidth, designHeight);
    applySoftShadingOverlay(
      ctx,
      baseImage,
      placement,
      shading.multiplyAlpha,
      shading.screenAlpha
    );
  }

  window.VanscapeGrosserBaumwollbeutelMockup = {
    defaultConfig: {
      assets: {
        baseSrc: 'assets/Mockup_grosser_baumwollbeutel.png',
        designSrc: 'assets/thumb_sketch.png'
      },
      placement: {
        x: 111,
        y: 366,
        width: 800,
        height: 550
      },
      warpOptions: {
        designScale: 0.95,
        designOffsetX: 0,
        designOffsetY: 0,
        topInsetLeft: 20,
        topInsetRight: 8,
        bottomInsetLeft: 8,
        bottomInsetRight: 22,
        topCurveLiftLeft: -24,
        topCurveLiftRight: -6,
        bottomCurveDropLeft: 10,
        bottomCurveDropRight: 18,
        leftSideBulge: 14,
        rightSideBulge: -8,
        verticalBendStrength: 0.18,
        shearX: 0.05,
        strips: 460,
        stripOverlap: 1.35,
        finalWarp: {
          topLeft: { x: 0, y: 0 },
          topRight: { x: 800, y: 0 },
          bottomRight: { x: 800, y: 550 },
          bottomLeft: { x: 0, y: 550 },
          handles: {
            topLeft: { x: 267, y: 0 },
            topRight: { x: 533, y: 0 },
            bottomRight: { x: 533, y: 550 },
            bottomLeft: { x: 267, y: 550 }
          }
        }
      },
      shading: {
        multiplyAlpha: 0.11,
        screenAlpha: 0.035
      }
    },
    renderToCanvas
  };
})();
